import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { X, Save, Trash2, Camera, Calendar, Clock, MapPin, FileText, Users, Wallet } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-100 focus:border-violet-500";

const STATUS_OPTS = [
  { value: "onay_bekleniyor", label: "Onay Bekleniyor", chip: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-400" },
  { value: "onaylandi", label: "Onaylandı", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  { value: "iptal", label: "İptal", chip: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
];

const DURATION_PRESETS = [
  { value: "morning", label: "09:30 – 12:30 (Sabah)", start: "09:30", end: "12:30" },
  { value: "afternoon", label: "14:30 – 17:30 (Öğleden Sonra)", start: "14:30", end: "17:30" },
  { value: "full", label: "09:30 – 17:30 (Tam Gün)", start: "09:30", end: "17:30" },
  { value: "custom", label: "Özel" },
];

export default function ShootModal({ workspace, initialDate, initialShoot, photographers, onClose, onSaved }) {
  const isEdit = !!initialShoot;
  const [brandQuery, setBrandQuery] = useState(initialShoot?.brand_name || "");
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [manualBrand, setManualBrand] = useState(initialShoot?.brand_source === "manual");
  const brandBoxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (brandBoxRef.current && !brandBoxRef.current.contains(e.target)) setShowSuggest(false); };
    const onEsc = (e) => { if (e.key === "Escape") setShowSuggest(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDocClick); document.removeEventListener("keydown", onEsc); };
  }, []);
  const [f, setF] = useState({
    brand_name: initialShoot?.brand_name || "",
    brand_source: initialShoot?.brand_source || "crm",
    date: initialShoot?.date || initialDate || new Date().toISOString().slice(0, 10),
    start_time: initialShoot?.start_time || "09:30",
    end_time: initialShoot?.end_time || "12:30",
    duration_preset: initialShoot?.duration_preset || "morning",
    location: initialShoot?.location || "",
    details: initialShoot?.details || "",
    photographer_id: initialShoot?.photographer_id || (photographers.find((p) => p.is_staff)?.id || ""),
    external_fee: initialShoot?.external_fee ?? 0,
    status: initialShoot?.status || "onay_bekleniyor",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selPh = photographers.find((p) => p.id === f.photographer_id);
  const isStaff = selPh?.is_staff;

  useEffect(() => {
    if (manualBrand) { setBrandSuggestions([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/calls-brands", { params: { q: brandQuery || "", limit: 30, workspace } });
        if (alive) setBrandSuggestions(data || []);
      } catch {}
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [brandQuery, manualBrand, workspace]);

  const pickSuggestion = (s) => {
    setF({ ...f, brand_name: s.brand_name, brand_source: "crm", location: s.location || f.location });
    setBrandQuery(s.brand_name);
    setShowSuggest(false);
  };

  const applyPreset = (val) => {
    const preset = DURATION_PRESETS.find((p) => p.value === val);
    if (!preset) return;
    if (val === "custom") { setF({ ...f, duration_preset: "custom" }); return; }
    setF({ ...f, duration_preset: val, start_time: preset.start, end_time: preset.end });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const brand = manualBrand ? brandQuery.trim() : (f.brand_name || brandQuery).trim();
    if (!brand) return setError("Firma adı zorunlu");
    if (!f.photographer_id) return setError("Çekimci seçilmeli");
    setSaving(true);
    try {
      const payload = {
        brand_name: brand,
        brand_source: manualBrand ? "manual" : f.brand_source,
        date: f.date,
        start_time: f.start_time,
        end_time: f.end_time,
        duration_preset: f.duration_preset,
        location: f.location,
        details: f.details,
        photographer_id: f.photographer_id,
        external_fee: isStaff ? 0 : parseFloat(f.external_fee || 0),
      };
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/shoots/${initialShoot.id}`, { ...payload, status: f.status });
        saved = data;
        toast.success("Çekim güncellendi");
      } else {
        const { data } = await api.post("/shoots", { workspace, ...payload });
        saved = data;
        toast.success("Çekim eklendi");
      }
      onSaved(saved);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Kaydedilemedi"));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`"${initialShoot.brand_name}" çekimi silinsin mi?`)) return;
    try {
      await api.delete(`/shoots/${initialShoot.id}`);
      toast.success("Çekim silindi");
      onSaved(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Silinemedi")); }
  };

  const changeStatus = async (v) => {
    if (!isEdit) { setF({ ...f, status: v }); return; }
    try {
      const { data } = await api.put(`/shoots/${initialShoot.id}`, { status: v });
      setF({ ...f, status: data.status });
      toast.success(v === "onaylandi" ? "Onaylandı, e-posta gönderildi" : v === "iptal" ? "İptal, e-posta gönderildi" : "Onay bekleniyor");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Güncellenemedi")); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center px-6 py-8 overflow-y-auto" data-testid="shoot-dialog">
      <div className="bg-white rounded-xl max-w-[720px] w-full p-6 my-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">{isEdit ? "Çekim Düzenle" : "Yeni Çekim"}</h3>
          <button onClick={onClose} data-testid="shoot-close"><X size={18} /></button>
        </div>

        {isEdit && (
          <div className="flex items-center gap-2 mb-4">
            {STATUS_OPTS.map((s) => {
              const on = f.status === s.value;
              return (
                <button key={s.value} type="button" onClick={() => changeStatus(s.value)} data-testid={`shoot-status-${s.value}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border inline-flex items-center gap-1.5 ${on ? s.chip : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  <span className={`w-2 h-2 rounded-full ${s.dot}`} />{s.label}
                </button>
              );
            })}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Brand */}
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Firma Adı</label>
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => { setManualBrand(false); setBrandQuery(f.brand_name); }} data-testid="shoot-brand-crm"
                className={`px-3 py-1.5 rounded-md text-xs font-medium ${!manualBrand ? "bg-violet-700 text-white" : "bg-slate-100 text-slate-600"}`}>CRM'den seç</button>
              <button type="button" onClick={() => setManualBrand(true)} data-testid="shoot-brand-manual"
                className={`px-3 py-1.5 rounded-md text-xs font-medium ${manualBrand ? "bg-violet-700 text-white" : "bg-slate-100 text-slate-600"}`}>Yeni firma (manuel)</button>
            </div>
            <div className="relative" ref={brandBoxRef}>
              <input type="text" value={brandQuery} onChange={(e) => { setBrandQuery(e.target.value); setShowSuggest(true); if (manualBrand) setF({ ...f, brand_name: e.target.value }); }}
                onFocus={() => setShowSuggest(true)} placeholder={manualBrand ? "Firma adını yazın" : "CRM'de ara..."} className={inp} data-testid="shoot-brand-input" />
              {!manualBrand && showSuggest && brandSuggestions.length > 0 && (
                <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {brandSuggestions.map((s) => (
                    <button key={s.brand_name} type="button" onClick={() => pickSuggestion(s)} data-testid={`shoot-brand-opt-${s.brand_name}`}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-none">
                      <div className="text-sm font-medium text-slate-900">{s.brand_name}</div>
                      <div className="text-xs text-slate-500">{s.contact_name || "—"} · {s.location || "Lokasyon yok"}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Calendar size={12} /> Tarih</label>
              <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} data-testid="shoot-date" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Clock size={12} /> Başlangıç</label>
              <input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value, duration_preset: "custom" })} className={inp} data-testid="shoot-start" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Bitiş</label>
              <input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value, duration_preset: "custom" })} className={inp} data-testid="shoot-end" /></div>
          </div>
          <div className="flex flex-wrap gap-2">
            {DURATION_PRESETS.map((p) => (
              <button key={p.value} type="button" onClick={() => applyPreset(p.value)} data-testid={`shoot-preset-${p.value}`}
                className={`px-3 py-1 rounded-full text-xs border ${f.duration_preset === p.value ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Location */}
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><MapPin size={12} /> Lokasyon / Adres</label>
            <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={inp} placeholder="Serbest metin" data-testid="shoot-location" />
          </div>

          {/* Details */}
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><FileText size={12} /> Çekim Detayları</label>
            <textarea rows={3} value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-100" placeholder="Notlar…" data-testid="shoot-details" />
          </div>

          {/* Photographer */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Users size={12} /> Çekimci</label>
              <select value={f.photographer_id} onChange={(e) => setF({ ...f, photographer_id: e.target.value })} className={inp} data-testid="shoot-photographer">
                {photographers.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.is_staff ? " (Staf)" : ""}</option>)}
              </select></div>
            {!isStaff && (
              <div><label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Wallet size={12} /> Dış Çekimci Ücreti (₺)</label>
                <input type="number" step="0.01" value={f.external_fee} onChange={(e) => setF({ ...f, external_fee: e.target.value })} className={inp} data-testid="shoot-fee" /></div>
            )}
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</div>}

          <div className="flex justify-between gap-2 pt-2 border-t border-slate-100 mt-4">
            <div>
              {isEdit && (
                <button type="button" onClick={remove} data-testid="shoot-delete"
                  className="rounded-full border border-red-200 text-red-600 hover:bg-red-50 px-5 py-2 text-sm inline-flex items-center gap-1.5">
                  <Trash2 size={13} /> Çekimi Sil
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2 text-sm">İptal</button>
              <button type="submit" disabled={saving} data-testid="shoot-save"
                className="rounded-full bg-violet-700 hover:bg-violet-600 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                <Save size={13} /> {saving ? "Kaydediliyor…" : "Kaydet"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
